import {
    Box,
    Button,
    VStack,
    Alert,
    AlertIcon,
    Input,
    FormControl,
    FormLabel,
    HStack,
    Spacer,
    FormHelperText,
    IconButton,
    Text,
    Flex
} from "@chakra-ui/react"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useToast } from "@chakra-ui/react"
import { useRouter } from "next/router"
import useKeywordListStore from "@/store/KeywordGroupListStore"
import useMemberStore from "@/store/MemberStore"
import { Plus, X } from "lucide-react"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"

function CreateKeywordComponent({ onClose }) {
    const t = useTranslations("KeywordCreate")
    const tCommon = useTranslations("Common")
    const [keywords, setKeywords] = useState([""]) // Array of keywords, start with one empty keyword
    const [operator, setOperator] = useState("OR") // Default operator
    const [isLoading, setIsLoading] = useState(false)
    const [showLimitAlert, setShowLimitAlert] = useState(false)
    const toast = useToast()
    const createKeywordGroupStore = useKeywordListStore(state => state.createKeywordGroup)
    const selectedAccount = useMemberStore(state => state.selectedAccount)
    const member = useMemberStore(state => state.member)
    const router = useRouter()

    const presentToast = (title, description, status, duration = 3000) => {
        toast({
            title,
            description,
            status,
            duration: 3000,
            isClosable: true,
        })
    }

    const handleClick = () => {
        setIsLoading(true)
        createKeywordGroup()
    }

    const handleKeywordChange = (index, value) => {
        const newKeywords = [...keywords]
        newKeywords[index] = value
        setKeywords(newKeywords)
    }

    const addKeywordInput = () => {
        if (keywords.length < 3) {
            setKeywords([...keywords, ""])
        }
    }

    const removeKeywordInput = (index) => {
        if (keywords.length > 1) {
            const newKeywords = keywords.filter((_, i) => i !== index)
            setKeywords(newKeywords)
        }
    }

    const handleCheckPlan = () => {
        router.push("/pricing")
    }

    const createKeywordGroup = async () => {
        try {
            setIsLoading(true)
            const selectedAccountInfo = member?.connectedThreadsAccounts?.find(
                account => account.username === selectedAccount
            )
            const threadsUserId = selectedAccountInfo?.threadsUserId
            const validKeywords = keywords.filter(keyword => keyword.trim())
            const result = await createKeywordGroupStore(validKeywords, operator, threadsUserId)

            if (result.success) {
                presentToast("", t("Toast.success"), "success", 5000)
                setKeywords([""])
                setOperator("OR")
                if (onClose) {
                    onClose()
                }
            } else if (result.error === 1520) {
                presentToast("", t("Toast.errors.duplicate"), "error")
            } else if (result.error === 1521) {
                setShowLimitAlert(true)
            } else {
                presentToast("", t("Toast.errors.unknown"), "error")
            }
        } catch (error) {
            presentToast("", t("Toast.errors.unknown"), "error")
        } finally {
            setIsLoading(false)
        }
    }

    const isFormValid = () => {
        const allKeywordsNonEmpty = keywords.every(keyword => keyword.trim().length > 0)
        return allKeywordsNonEmpty && keywords.length >= 1
    }

    return (
        <Box>
            <AlertDialog open={showLimitAlert} onOpenChange={setShowLimitAlert}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("Title.limitReached")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("Message.limitReachedDescription")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("Action.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCheckPlan}>{t("Action.checkPlan")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Box mx="auto" w="100%" align="center">
                <VStack spacing="16px" marginLeft="4px" marginRight="4px">
                    <Box width="100%" align="left">
                        <VStack spacing="16px">
                            <FormControl>
                                <VStack spacing="12px" align="stretch">
                                    {keywords.map((keyword, index) => (
                                        <Flex key={index} align="center" gap="8px">
                                            <Input
                                                value={keyword}
                                                onChange={(e) => handleKeywordChange(index, e.target.value)}
                                                placeholder={t("Hint.keywordPlaceholder", { index: index + 1 })}
                                                flex="1"
                                            />
                                            {keywords.length > 1 && (
                                                <IconButton
                                                    aria-label="Remove keyword"
                                                    icon={<X size={16} />}
                                                    size="sm"
                                                    variant="outline"
                                                    colorScheme="red"
                                                    onClick={() => removeKeywordInput(index)}
                                                />
                                            )}
                                            {index < keywords.length - 1 && (
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="bold"
                                                    color="blue.500"
                                                    minW="30px"
                                                    textAlign="center"
                                                >
                                                    {operator === "OR" ? t("Hint.operatorOr") : t("Hint.operatorAnd")}
                                                </Text>
                                            )}
                                        </Flex>
                                    ))}

                                    {keywords.length < 3 && (
                                        <Button
                                            leftIcon={<Plus size={16} />}
                                            variant="outline"
                                            size="sm"
                                            onClick={addKeywordInput}
                                            alignSelf="flex-start"
                                        >
                                            {t("Action.add")}
                                        </Button>
                                    )}
                                </VStack>
                                <FormHelperText>
                                    {t("Hint.keywordLimit")}
                                </FormHelperText>
                            </FormControl>

                            {keywords.length > 1 && (
                                <FormControl>
                                    {/* <FormLabel>Logic Operator</FormLabel> */}
                                    <Select value={operator} onValueChange={setOperator}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="OR">{t("Description.operatorOr")}</SelectItem>
                                            <SelectItem value="AND">{t("Description.operatorAnd")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    {/* <FormHelperText>
                                        {operator === "OR"
                                            ? "貼文匹配任何關鍵字都會被捕捉"
                                            : "貼文必須包含所有關鍵字才會被捕捉"
                                        }
                                    </FormHelperText> */}
                                </FormControl>
                            )}

                            {/* <FormControl> */}
                            {/* <FormLabel>{t("Form.frequency.label")}</FormLabel>
                                <Select onValueChange={setPurpose}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t("Form.frequency.placeholder")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(FREQUENCY_OPTIONS).map(([value, { label }]) => (
                                            <SelectItem key={value} value={value}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormHelperText>{getHelperText()}</FormHelperText>
                            </FormControl> */}
                        </VStack>
                        <HStack>
                            <Spacer />
                            {onClose && (
                                <Button
                                    marginTop="16px"
                                    variant="outline"
                                    onClick={onClose}
                                >
                                    {t("Action.cancel")}
                                </Button>
                            )}
                            <Button
                                marginTop="16px"
                                background="main.red"
                                onClick={handleClick}
                                isLoading={isLoading}
                                isDisabled={!isFormValid()}>
                                {t("Action.startTracking")}
                            </Button>
                        </HStack>
                    </Box>
                </VStack>
            </Box>
        </Box>
    )
}

export default CreateKeywordComponent